import type { CrmCaseType } from "@shared/types/crm";
import type {
  SmartFieldTemplate,
  CampaignSmartField,
  CreateCustomSmartFieldData,
} from "@shared/types/smartFields";

import { defineStore } from "pinia";
import { ref, computed } from "vue";

import apiClient from "@/api/axios";
import { getErrorMessage } from "@/utils/errorHandling";

export interface AdminCampaign {
  activeSyncId?: null | string;
  category?: null | string;
  color?: null | string;
  createdAt: string;
  createdByOrgId?: null | string; // owner org — use authStore.isCampaignOwner() to gate actions
  crmCaseTypeId?: null | string;
  crmConfigId?: null | string; // CRM config ID
  crmType?: null | string; // CRM type (e.g., "NEOS", "SMART_ADVOCATE")
  customSyncerKey?: null | string; // custom syncer key when crmType = CUSTOM (e.g., "sokolove_domo")
  excludedCases: number;
  failedCaseCount: number; // Total failed (backwards compatibility)
  falloutCases: number;
  hasFailedCases?: boolean;
  id: string;
  isSyncing: boolean;
  lastSyncedAt: null | string;
  name: string;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. */
  onHoldCases: number;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. */
  openCases: number;
  pendingRetryCount: number; // Cases with 1-9 failures
  permanentlyFailedCount: number; // Cases with 10+ failures
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. */
  settledCases: number;
  status: string;
  /** Interval the scheduler actually runs this campaign on, in hours; null = once a day. */
  syncIntervalHours: null | number;
  /** Whether the scheduler syncs this campaign at all (active, owner syncing, CRM-fed). */
  syncScheduled: boolean;
  totalCases: number;
  type: string;
  updatedAt: string;
}

/** GET /admin/campaigns/:id/edit-details. */
export interface CampaignEditDetails {
  category?: null | string;
  color?: null | string;
  // Free-form CRM metadata JSON (SyncConfig, CaseTypeId, crmType, ...).
  crmMetadata?: null | Record<string, unknown>;
  defaultLegalFeePercent?: null | number;
  description?: null | string;
  id: string;
  isDemo: boolean;
  lastSyncedAt?: null | string;
  name: string;
  /** The source's interval a campaign with no override follows; absent = no pull source. */
  sourceSyncIntervalHours?: null | number;
  status: string;
  /** The campaign's own override; null = follow the source. */
  syncIntervalHours: null | number;
  type: string;
}

// Interface for field mapping
export interface FieldMapping {
  campaignId: string;
  crmFieldKey: null | string;
  entityType: string; // "Client" or "Case"
  id: string;
  shareWithReferring: boolean;
  standardField: {
    columnName?: string;
    dataType: string;
    displayName: string;
    fieldKey: string;
    id: string;
    isDefault?: boolean;
    isHardCoded: boolean;
    isPII?: boolean;
  };
  standardFieldId: string;
  visibleToOrgs: string[];
}

// Type for PUT /field-mappings request body (what we SEND to the API)
export interface FieldMappingInput {
  crmFieldKey?: string;
  isPII: boolean;
  shareWithReferring?: boolean;
  standardFieldId: string;
  visibleToOrgs?: string[];
}

// Type for POST /field-mapping and PATCH /field-mappings/:id request body
export interface SingleFieldMappingInput {
  crmFieldKey?: string;
  entityType?: "Case" | "Client";
  id?: string; // For updates (PATCH)
  isPII?: boolean;
  shareWithReferring?: boolean;
  standardFieldId: string;
  updateIsPII?: boolean;
  updateStandardField?: boolean;
  visibleToOrgs?: string[];
}

// Interface for standard field
export interface StandardField {
  category?: string;
  dataType: string;
  description?: string;
  displayName: string;
  // CLIENT | CASE — used to group the ingestion field-mapping picker.
  entityType: string;
  fieldKey: string;
  id: string;
  // Canonical schema fields are hardcoded columns or seeded defaults; ad-hoc per-source
  // custom fields are neither. The ingestion mapping picker lists only the canonical ones.
  isDefault: boolean;
  isHardCoded: boolean;
  isPII: boolean;
  visibilityScope: string;
}

export interface CampaignCaseSearchRow {
  caseId: string;
  caseNumber: null | string;
  clientFirstName: null | string;
  clientLastName: null | string;
  id: string;
}

// Interface for schema column
export interface SchemaColumn {
  isRequired: boolean;
  isUnique: boolean;
  name: string;
  type: string;
}

// Interface for schema columns response
export interface SchemaColumnsResponse {
  case: SchemaColumn[];
  client: SchemaColumn[];
}

// Column metadata from backend (derived from Prisma DMMF + standard field constants)
export interface ColumnMeta {
  dataType: string;
  entity: "CASE" | "CLIENT";
  format?: string;
}

export const useAdminStore = defineStore("admin", () => {
  // State
  const campaigns = ref<AdminCampaign[]>([]);
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const campaignDetails = ref<any>(null);
  const fieldMappings = ref<FieldMapping[]>([]);
  const standardFields = ref<StandardField[]>([]);
  const crmCaseTypes = ref<CrmCaseType[]>([]);
  const isLoadingCaseTypes = ref(false);
  const caseTypesError = ref<null | string>(null);
  const isLoading = ref(false);
  const error = ref<null | string>(null);
  // The campaign list's own flags. The shared isLoading/error above are set by every action, so
  // a page that swapped its table for a spinner on them lost it whenever anything else ran.
  const isLoadingCampaigns = ref(false);
  const campaignsError = ref<null | string>(null);
  // Whether a list has ever arrived. An empty list can't stand in for this: an org with no
  // campaigns, or one that just deleted its last, has loaded.
  const hasLoadedCampaigns = ref(false);

  // Schema columns state (cached)
  const schemaColumns = ref<null | SchemaColumnsResponse>(null);
  const columnMeta = ref<Record<string, ColumnMeta>>({});
  const isLoadingSchemaColumns = ref(false);
  const schemaColumnsError = ref<null | string>(null);

  // Smart Fields state
  const smartFieldTemplates = ref<SmartFieldTemplate[]>([]);
  const campaignSmartFields = ref<CampaignSmartField[]>([]);
  const isLoadingSmartFields = ref(false);
  const smartFieldsError = ref<null | string>(null);

  // Campaign case search cache (keyed by campaignId)
  const campaignCases = ref<Record<string, CampaignCaseSearchRow[]>>({});

  // Bumped by $reset. Every fetch that writes a ref after its await checks it, so a response from
  // an older generation (the previous user's, still in flight at sign-out) is never written into
  // a store the next user reads. Components render these refs directly, not just the return value.
  let generation = 0;
  const STALE_RESPONSE = "Signed out while this was loading";

  const fetchCampaignCases = async (
    campaignId: string,
    options: { force?: boolean } = {}
  ): Promise<CampaignCaseSearchRow[]> => {
    if (!options.force && campaignCases.value[campaignId]) {
      return campaignCases.value[campaignId];
    }
    const requestGeneration = generation;
    const response = await apiClient.get<CampaignCaseSearchRow[]>(
      `/campaigns/${campaignId}/case-search`
    );
    if (requestGeneration !== generation) return [];
    campaignCases.value[campaignId] = response.data;
    return response.data;
  };

  // CRM Configs state (cached)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const crmConfigs = ref<any[]>([]);
  const crmConfigsLoaded = ref(false);

  // Actions
  // Only the latest list request may write: an older one (a background refetch from a sync's SSE
  // stream) landing late would otherwise undo a row setCampaignStatus just patched.
  let campaignsRequest = 0;
  // Bumped by every local write to `campaigns` (status patch, edit, delete). A list read that started before a patch can carry the row's
  // old value even when it's the latest request, so it's dropped and the list read again.
  let campaignEdits = 0;
  const fetchAdminCampaigns = async (): Promise<void> => {
    const request = ++campaignsRequest;
    const edits = campaignEdits;
    try {
      isLoadingCampaigns.value = true;
      campaignsError.value = null;
      const response = await apiClient.get<AdminCampaign[]>("/admin/campaigns");
      if (request !== campaignsRequest) return;
      if (edits !== campaignEdits) return await fetchAdminCampaigns();
      campaigns.value = response.data;
      hasLoadedCampaigns.value = true;
    } catch (err) {
      if (request !== campaignsRequest) return;
      campaignsError.value = getErrorMessage(err, "Failed to fetch campaigns");
    } finally {
      if (request === campaignsRequest) isLoadingCampaigns.value = false;
    }
  };

  /**
   * Back to the initial state, dropping every org's data. Pinia only gives options stores a
   * `$reset`; on a setup store it's a no-op in production, so auth's clearAllStores (sign-out, or
   * a magic-link account switch with no page reload) left the last user's campaigns here.
   */
  const $reset = (): void => {
    campaignsRequest++; // a list request still in flight belongs to the previous user
    generation++;
    campaigns.value = [];
    campaignDetails.value = null;
    fieldMappings.value = [];
    standardFields.value = [];
    crmCaseTypes.value = [];
    isLoadingCaseTypes.value = false;
    caseTypesError.value = null;
    isLoading.value = false;
    error.value = null;
    isLoadingCampaigns.value = false;
    campaignsError.value = null;
    hasLoadedCampaigns.value = false;
    schemaColumns.value = null;
    columnMeta.value = {};
    isLoadingSchemaColumns.value = false;
    schemaColumnsError.value = null;
    smartFieldTemplates.value = [];
    campaignSmartFields.value = [];
    isLoadingSmartFields.value = false;
    smartFieldsError.value = null;
    campaignCases.value = {};
    crmConfigs.value = [];
    crmConfigsLoaded.value = false;
  };

  // Fetch a single campaign
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchCampaign = async (campaignId: string): Promise<any> => {
    const requestGeneration = generation;
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.get(`/campaigns/${campaignId}`);
      if (requestGeneration !== generation) throw new Error(STALE_RESPONSE);
      campaignDetails.value = response.data;
      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (requestGeneration !== generation) throw err;
      error.value = err.message || "Failed to fetch campaign";
      isLoading.value = false;
      throw err;
    }
  };

  // Fetch campaign details optimized for editing
  const fetchCampaignForEditing = async (campaignId: string): Promise<CampaignEditDetails> => {
    const requestGeneration = generation;
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.get(`/admin/campaigns/${campaignId}/edit-details`);
      // Throw rather than return it: a caller still mounted across a magic-link switch would
      // render the previous org's campaign in its own form state.
      if (requestGeneration !== generation) throw new Error(STALE_RESPONSE);
      campaignDetails.value = response.data;
      isLoading.value = false;

      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (requestGeneration !== generation) throw err;
      error.value = err.message || "Failed to fetch campaign details";
      isLoading.value = false;
      throw err;
    }
  };

  // Update a campaign
  // TODO: Fix this the next time the file is edited.
  /**
   * Apply a status toggle to the loaded row in place, with the server's recomputed
   * syncScheduled (it depends on status). A refetch would flip isLoading, which swaps the
   * dashboard's table for a spinner. Returns false when the row isn't loaded.
   */
  const setCampaignStatus = (
    campaignId: string,
    status: "ACTIVE" | "INACTIVE",
    syncScheduled: boolean
  ): boolean => {
    const campaign = campaigns.value.find((c) => c.id === campaignId);
    if (!campaign) return false;
    campaign.status = status;
    campaign.syncScheduled = syncScheduled;
    campaignEdits++;
    return true;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateCampaign = async (campaignId: string, campaignData: any): Promise<any> => {
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.put(`/admin/campaigns/${campaignId}`, campaignData);

      // Update the campaign in the local state
      if (response.data.success) {
        const index = campaigns.value.findIndex((c) => c.id === campaignId);
        if (index >= 0) {
          campaigns.value[index] = {
            ...campaigns.value[index],
            ...campaignData,
          };
          campaignEdits++;
        }
        // Also update campaign details if loaded
        if (campaignDetails.value?.id === campaignId) {
          campaignDetails.value = { ...campaignDetails.value, ...campaignData };
        }
      }

      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = err.message || "Failed to update campaign";
      isLoading.value = false;
      throw err;
    }
  };

  // Fetch field mappings for a campaign
  const fetchFieldMappings = async (campaignId: string): Promise<FieldMapping[]> => {
    const requestGeneration = generation;
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.get(`/crm/campaigns/${campaignId}/field-mappings`);
      if (requestGeneration !== generation) return [];
      fieldMappings.value = response.data.mappings;
      isLoading.value = false;
      return response.data.mappings;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (requestGeneration !== generation) return [];
      error.value = err.message || "Failed to fetch field mappings";
      isLoading.value = false;
      return [];
    }
  };

  // Fetch standard fields
  const fetchStandardFields = async (): Promise<StandardField[]> => {
    const requestGeneration = generation;
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.get("/crm/standard-fields");
      if (requestGeneration !== generation) return [];
      standardFields.value = response.data;
      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (requestGeneration !== generation) return [];
      error.value = err.message || "Failed to fetch standard fields";
      isLoading.value = false;
      return [];
    }
  };

  // Fetch schema columns (mappable database columns from Prisma schema)
  const fetchSchemaColumns = async (): Promise<null | SchemaColumnsResponse> => {
    const requestGeneration = generation;
    try {
      isLoadingSchemaColumns.value = true;
      schemaColumnsError.value = null;

      const response = await apiClient.get<{
        columnMeta?: Record<string, ColumnMeta>;
        columns: SchemaColumnsResponse;
        success: boolean;
      }>("/admin/fields/schema-columns");
      if (requestGeneration !== generation) return null;

      schemaColumns.value = response.data.columns;
      if (response.data.columnMeta) {
        columnMeta.value = response.data.columnMeta;
      }
      return schemaColumns.value;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (requestGeneration !== generation) return null;
      schemaColumnsError.value =
        err.response?.data?.error || err.message || "Failed to fetch schema columns";
      return null;
    } finally {
      if (requestGeneration === generation) isLoadingSchemaColumns.value = false;
    }
  };

  // Save field mappings
  const saveFieldMappings = async (
    campaignId: string,
    mappings: FieldMappingInput[]
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> => {
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.put(`/crm/campaigns/${campaignId}/field-mappings`, {
        mappings,
      });

      // Only update local state if the response contains mappings
      if (response.data.mappings && response.data.mappings.length > 0) {
        fieldMappings.value = response.data.mappings;
      } else {
        // If the response returns an empty array, re-fetch the mappings
        // This prevents the UI from blanking out when no changes were detected by the API
        await fetchFieldMappings(campaignId);
      }

      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = err.message || "Failed to save field mappings";
      isLoading.value = false;
      throw err;
    }
  };

  // Delete a field mapping
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deleteFieldMapping = async (campaignId: string, mappingId: string): Promise<any> => {
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.delete(
        `/crm/campaigns/${campaignId}/field-mappings/${mappingId}`
      );

      // Update local state
      if (response.data.success) {
        fieldMappings.value = fieldMappings.value.filter((m) => m.id !== mappingId);
      }

      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = err.message || "Failed to delete field mapping";
      isLoading.value = false;
      throw err;
    }
  };

  // Trigger a campaign sync with CRM
  const syncCampaign = async (
    campaignId: string,
    force: boolean = false
  ): Promise<{
    message: string;
    success: boolean;
    syncId: string;
  }> => {
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.post(`/crm/campaigns/${campaignId}/sync?force=${force}`);

      // The response now returns a syncId instead of results
      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = err.message || "Failed to sync campaign";
      isLoading.value = false;
      throw err;
    }
  };

  // NEW: Trigger a parallel campaign sync with CRM
  const parallelSyncCampaign = async (
    campaignId: string,
    concurrency: number = 10,
    maxRetries: number = 3
  ): Promise<{
    message: string;
    success: boolean;
    syncId: string;
  }> => {
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.post(
        `/crm/campaigns/${campaignId}/sync?parallel=true&concurrency=${concurrency}&maxRetries=${maxRetries}`
      );

      // The response now returns a syncId instead of results
      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = err.message || "Failed to parallel sync campaign";
      isLoading.value = false;
      throw err;
    }
  };

  // Get sync progress
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getSyncStatus = async (syncId: string): Promise<any> => {
    try {
      // Extract campaign ID from sync ID (assumed format: "campaign-id-sync-uuid")
      const campaignId = syncId.split("-")[0];
      const response = await apiClient.get(`/crm/sync/${campaignId}/status/${syncId}`);
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      throw err;
    }
  };

  // Delete a campaign
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deleteCampaign = async (campaignId: string): Promise<any> => {
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.delete(`/admin/campaigns/${campaignId}`);

      // Update local state
      if (response.data.success) {
        campaigns.value = campaigns.value.filter((c) => c.id !== campaignId);
        campaignEdits++;
      }

      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = err.message || "Failed to delete campaign";
      isLoading.value = false;
      throw err;
    }
  };

  // Create a new campaign
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const createCampaign = async (campaignData: any): Promise<any> => {
    try {
      isLoading.value = true;
      error.value = null;
      const response = await apiClient.post("/admin/campaigns", campaignData);

      // Update local state with new campaign
      if (response.data.success) {
        await fetchAdminCampaigns(); // Refresh the campaigns list
      }

      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = err.message || "Failed to create campaign";
      isLoading.value = false;
      throw err;
    }
  };

  // Fetch CRM case types with caching
  const fetchCrmCaseTypes = async (
    forceRefresh = false,
    crmConfigId?: null | string
  ): Promise<CrmCaseType[]> => {
    // Return cached case types if available and not forcing refresh
    // But only if we're not filtering by a specific CRM config
    if (crmCaseTypes.value.length > 0 && !forceRefresh && !crmConfigId) {
      return crmCaseTypes.value;
    }

    const requestGeneration = generation;
    try {
      isLoadingCaseTypes.value = true;
      caseTypesError.value = null;

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const params: any = {};
      if (crmConfigId) {
        params.crmConfigId = crmConfigId;
      }

      const response = await apiClient.get<CrmCaseType[]>("/crm/casetypes", { params });
      if (requestGeneration !== generation) return [];
      crmCaseTypes.value = response.data || [];

      return crmCaseTypes.value;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (requestGeneration !== generation) throw err;
      caseTypesError.value =
        err.response?.data?.message || err.message || "Failed to load CRM case types.";
      // Rethrow so the caller can tell which request failed: with several sources in
      // flight, this shared error ref can't say whose failure it holds.
      throw err;
    } finally {
      if (requestGeneration === generation) isLoadingCaseTypes.value = false;
    }
  };

  // Get a case type by ID (from cache)
  const getCaseTypeById = (id: string): CrmCaseType | null => {
    return crmCaseTypes.value.find((type) => type.Id === id) || null;
  };

  // Fetch CRM configurations for the organization (cached)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchCrmConfigs = async (forceRefresh = false): Promise<any[]> => {
    // Return cached data if available and not forcing refresh
    if (crmConfigsLoaded.value && !forceRefresh) {
      return crmConfigs.value;
    }

    const requestGeneration = generation;
    try {
      const response = await apiClient.get("/admin/crm-configs");
      if (requestGeneration !== generation) return [];
      crmConfigs.value = response.data || [];
      crmConfigsLoaded.value = true;
      return crmConfigs.value;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      throw err;
    }
  };

  // Clear CRM configs cache (call after updating config)
  const clearCrmConfigsCache = () => {
    crmConfigs.value = [];
    crmConfigsLoaded.value = false;
  };

  // Getters
  const getAllCampaigns = computed(() => campaigns.value);

  // Clear error
  const clearError = () => {
    error.value = null;
  };

  // Update or create a single field mapping
  const updateSingleFieldMapping = async (
    campaignId: string,
    mapping: SingleFieldMappingInput
  ): Promise<FieldMapping> => {
    try {
      isLoading.value = true;
      error.value = null;

      let response;
      // If ID exists, it's an update, otherwise it's a create
      if (mapping.id) {
        // For updates, only send editable fields to prevent unintended overwrites
        const updateData = {
          // Optional - can be derived from visibleToOrgs on server
          shareWithReferring: mapping.visibleToOrgs?.includes("REFERRING") || false,
          standardFieldId: mapping.standardFieldId, // Required by the backend validation schema even though we're not changing it
          visibleToOrgs: mapping.visibleToOrgs || [],
        };

        response = await apiClient.patch(
          `/crm/campaigns/${campaignId}/field-mappings/${mapping.id}`,
          updateData
        );
      } else {
        // For new mappings, only send fields used by the backend
        // Note: crmFieldKey and isPII are NOT used (backend policy: never modify StandardCustomField from this endpoint)
        const createData = {
          entityType: mapping.entityType || null,
          shareWithReferring: mapping.visibleToOrgs?.includes("REFERRING") || false,
          standardFieldId: mapping.standardFieldId,
          visibleToOrgs: mapping.visibleToOrgs || ["HANDLING"],
        };

        response = await apiClient.post(`/crm/campaigns/${campaignId}/field-mapping`, createData);
      }

      // Update the local state
      if (response.data) {
        // If updating an existing mapping
        if (mapping.id) {
          const index = fieldMappings.value.findIndex((m) => m.id === mapping.id);
          if (index !== -1) {
            fieldMappings.value[index] = response.data;
          }
        } else {
          // If adding a new mapping
          fieldMappings.value.push(response.data);
        }
      }

      isLoading.value = false;
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = err.message || "Failed to update field mapping";
      isLoading.value = false;
      throw err;
    }
  };

  // Smart Fields actions
  const fetchSmartFieldTemplates = async () => {
    const requestGeneration = generation;
    isLoadingSmartFields.value = true;
    smartFieldsError.value = null;
    try {
      const response = await apiClient.get("/admin/smart-fields/templates", {
        params: { status: "PUBLISHED" },
      });
      if (requestGeneration !== generation) return [];
      smartFieldTemplates.value = response.data.templates || [];
      return smartFieldTemplates.value;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (requestGeneration !== generation) return [];
      smartFieldsError.value =
        error.response?.data?.error || "Failed to fetch Smart Field templates";
      return [];
    } finally {
      if (requestGeneration === generation) isLoadingSmartFields.value = false;
    }
  };

  const fetchCampaignSmartFields = async (campaignId: string) => {
    const requestGeneration = generation;
    isLoadingSmartFields.value = true;
    smartFieldsError.value = null;
    try {
      const response = await apiClient.get(`/campaigns/${campaignId}/smart-fields`);
      if (requestGeneration !== generation) return [];
      campaignSmartFields.value = response.data.smartFields || [];
      return campaignSmartFields.value;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (requestGeneration !== generation) return [];
      smartFieldsError.value =
        error.response?.data?.error || "Failed to fetch campaign Smart Fields";
      return [];
    } finally {
      if (requestGeneration === generation) isLoadingSmartFields.value = false;
    }
  };

  const addLibraryFieldToCampaign = async (campaignId: string, templateId: string) => {
    try {
      const response = await apiClient.post(`/campaigns/${campaignId}/smart-fields/from-library`, {
        templateId,
      });
      // Refresh campaign smart fields
      await fetchCampaignSmartFields(campaignId);
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message = error.response?.data?.error || "Failed to add library field";
      throw new Error(message);
    }
  };

  const createCustomSmartField = async (
    campaignId: string,
    fieldData: CreateCustomSmartFieldData
  ) => {
    try {
      const response = await apiClient.post(
        `/campaigns/${campaignId}/smart-fields/custom`,
        fieldData
      );
      // Refresh campaign smart fields
      await fetchCampaignSmartFields(campaignId);
      return response.data;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message = error.response?.data?.error || "Failed to create custom Smart Field";
      throw new Error(message);
    }
  };

  const testRunSmartField = async (
    campaignId: string,
    params: {
      allowedValues?: string[];
      caseId?: string;
      defaultValue?: string;
      format: string;
      prompt: string;
    }
  ) => {
    const response = await apiClient.post(`/campaigns/${campaignId}/smart-fields/test-run`, params);
    return response.data;
  };

  const removeSmartField = async (campaignSmartFieldId: string, campaignId: string) => {
    try {
      await apiClient.delete(`/campaigns/${campaignId}/smart-fields/${campaignSmartFieldId}`);
      // Refresh campaign smart fields
      await fetchCampaignSmartFields(campaignId);
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message = error.response?.data?.error || "Failed to remove Smart Field";
      throw new Error(message);
    }
  };

  const toggleSmartField = async (
    campaignSmartFieldId: string,
    campaignId: string,
    isActive: boolean
  ) => {
    try {
      await apiClient.put(`/campaigns/${campaignId}/smart-fields/${campaignSmartFieldId}`, {
        isActive,
      });
      // Refresh campaign smart fields
      await fetchCampaignSmartFields(campaignId);
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message = error.response?.data?.error || "Failed to toggle Smart Field";
      throw new Error(message);
    }
  };

  const updateSmartField = async (
    campaignSmartFieldId: string,
    campaignId: string,
    updates: {
      allowedValues?: string[];
      defaultValue?: null | string;
      format?: string;
      isActive?: boolean;
      isPII?: boolean;
      name?: string;
      prompt?: string;
      refreshCadence?: null | string;
      runOnce?: boolean | null;
    }
  ) => {
    try {
      await apiClient.put(`/campaigns/${campaignId}/smart-fields/${campaignSmartFieldId}`, updates);
      // Refresh campaign smart fields
      await fetchCampaignSmartFields(campaignId);
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message = error.response?.data?.error || "Failed to update Smart Field";
      throw new Error(message);
    }
  };

  const reorderCampaignSmartFields = async (campaignId: string, orderedIds: string[]) => {
    try {
      const smartFields = orderedIds.map((id, index) => ({
        displayOrder: index + 1,
        id,
      }));

      await apiClient.post(`/campaigns/${campaignId}/smart-fields/update-order`, {
        smartFields,
      });
      // Refresh campaign smart fields to get updated order
      await fetchCampaignSmartFields(campaignId);
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const message = error.response?.data?.error || "Failed to reorder Smart Fields";
      throw new Error(message);
    }
  };

  return {
    $reset,
    campaignCases,
    campaignDetails,
    // State
    campaigns,
    campaignsError,
    campaignSmartFields,
    caseTypesError,
    columnMeta,
    crmCaseTypes,
    error,
    fieldMappings,
    hasLoadedCampaigns,
    isLoading,
    isLoadingCampaigns,
    isLoadingCaseTypes,
    isLoadingSchemaColumns,
    isLoadingSmartFields,
    schemaColumns,
    schemaColumnsError,
    smartFieldsError,
    smartFieldTemplates,
    standardFields,

    addLibraryFieldToCampaign,
    clearCrmConfigsCache,
    clearError,
    createCampaign,
    createCustomSmartField,
    crmConfigs,
    crmConfigsLoaded,
    deleteCampaign,
    deleteFieldMapping,
    // Actions
    fetchAdminCampaigns,
    fetchCampaign,
    fetchCampaignCases,
    fetchCampaignForEditing,
    fetchCampaignSmartFields,
    fetchCrmCaseTypes,
    fetchCrmConfigs,
    fetchFieldMappings,
    fetchSchemaColumns,
    fetchSmartFieldTemplates,
    fetchStandardFields,
    getCaseTypeById,
    getSyncStatus,
    parallelSyncCampaign,
    removeSmartField,
    reorderCampaignSmartFields,
    saveFieldMappings,
    setCampaignStatus,
    syncCampaign,
    testRunSmartField,
    toggleSmartField,
    updateCampaign,
    updateSingleFieldMapping,
    updateSmartField,

    // Getters
    getAllCampaigns,
  };
});
